import { Box, Chip, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Confirm, Report } from 'notiflix';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import GiftMovesModal from '../modal/GiftMovesModal';
import DeliverMovesModal from '../modal/DeliverMovesModal';
import userAvatar from '../../assets/img/User-Avatar.png';
import { ROLE_IDS, isAdmin, isCompany } from '../../config/roles';
import { updateUser } from '../../data/api/users/UserRoutes';
import useIndex from '../../hooks/useIndex';
import validErrors from '../../hooks/validErrors';

const roleColors = {
    Administrador: '#10285f',
    Jugador: '#3490dc',
    Empresa: '#2ecc71',
    Distribuidor: '#e67e22',
    Public: '#999',
};

const UserCard = ({ user, onAssignMoves, onChanged }) => {
    const navigate = useNavigate();
    const { auth, token } = useIndex();

    const empresa = isCompany(auth);
    const admin = isAdmin(auth);
    const serverUrl = process.env.REACT_APP_BACK_URL;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [giftOpen, setGiftOpen] = useState(false);
    const [deliverOpen, setDeliverOpen] = useState(false);

    // Toggle de acceso al usuario desde la lista. Solo admin, y nunca
    // sobre uno mismo (eso evita que un admin se deje afuera del sistema
    // por accidente desde el listado).
    const isSelf = auth?.id === user?.id;
    const isUserDisabled = Number(user?.status) === 0;
    const canToggleAccess = admin && !isSelf;

    // Regalo: solo admin sobre un Jugador activo (carece de sentido
    // regalarle jugadas a una empresa o distribuidor).
    const isPlayer = Number(user?.role_id) === ROLE_IDS.JUGADOR;
    const canGift = admin && isPlayer && !isUserDisabled;

    // Entregar jugadas: solo admin sobre un Distribuidor activo. Genera
    // jugadas entregadas (facturadas) + de regalo (bonus, sin factura).
    const isDistributor = Number(user?.role_id) === ROLE_IDS.DISTRIBUIDOR;
    const canDeliver = admin && isDistributor && !isUserDisabled;

    const roleColor = roleColors[user?.role] ?? '#10285f';

    const avatarSrc = user?.avatar
        ? `${serverUrl}${user.avatar}`
        : userAvatar;

    const goToProfile = () => {
        navigate(`/ganacol/dashboard/users/${user?.uuid}`);
    };

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const sendToggleAccess = async () => {
        const willDisable = !isUserDisabled;
        try {
            const { data } = await updateUser(token, user?.uuid, {
                name: user?.name,
                lastname: user?.lastname,
                email: user?.email,
                phone: user?.phone,
                document: user?.document,
                document_type_id: user?.document_type_id,
                role_id: user?.role_id,
                company_id: user?.company_id ?? null,
                status: willDisable ? 0 : 1,
                game_enabled: willDisable ? 0 : 1,
            });
            if (data?.success) {
                Report.success(
                    willDisable ? 'Usuario deshabilitado' : 'Usuario habilitado',
                    data?.message ?? (willDisable
                        ? 'El usuario ya no tiene acceso al sistema ni puede jugar.'
                        : 'El usuario recuperó su acceso y permiso para jugar.'),
                    'Aceptar',
                    { textAlign: 'center' },
                );
                onChanged?.();
            }
        } catch (error) {
            validErrors(error);
        }
    };

    const handleToggleAccess = () => {
        handleClose();
        const willDisable = !isUserDisabled;
        Confirm.show(
            willDisable ? 'Deshabilitar usuario' : 'Habilitar usuario',
            willDisable
                ? `¿Seguro que deseas deshabilitar a ${user?.name ?? 'este usuario'}? No podrá acceder al sistema ni jugar hasta que lo reactives.`
                : `¿Seguro que deseas habilitar a ${user?.name ?? 'este usuario'}? Recuperará acceso al sistema y permiso para jugar.`,
            willDisable ? 'Sí, deshabilitar' : 'Sí, habilitar',
            'Cancelar',
            sendToggleAccess,
            () => { },
        );
    };

    const stats = [
        {
            icon: 'fas fa-chart-line',
            label: 'Juegos',
            value: user?.games ?? 0,
        },
        {
            icon: 'fas fa-calendar',
            label: 'Días',
            value: user?.user_days ?? 1,
        },
        {
            icon: 'fas fa-wallet',
            label: 'Códigos',
            value: user?.moves ?? 0,
        },
        {
            icon: 'fas fa-trophy',
            label: 'Aciertos',
            value: user?.hits ?? 0,
        },
    ];

    return (
        <>
        <UserCardContent $roleColor={roleColor} $disabled={isUserDisabled}>
            <Box className="top-accent" />

            <Box className="options">
                <Tooltip title="Opciones">
                    <IconButton size="small" onClick={handleClick}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Menu
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem onClick={goToProfile}>
                        Perfil
                    </MenuItem>

                    {empresa && (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                onAssignMoves?.(user);
                            }}
                        >
                            Asignar jugadas
                        </MenuItem>
                    )}

                    {canGift && (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                setGiftOpen(true);
                            }}
                            sx={{ gap: 1, color: '#10285f', fontWeight: 700 }}
                        >
                            <RedeemOutlinedIcon fontSize="small" />
                            Regalo
                        </MenuItem>
                    )}

                    {canDeliver && (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                setDeliverOpen(true);
                            }}
                            sx={{ gap: 1, color: '#d35400', fontWeight: 700 }}
                        >
                            <LocalShippingOutlinedIcon fontSize="small" />
                            Entregar Jugadas
                        </MenuItem>
                    )}

                    {canToggleAccess && (
                        <MenuItem
                            onClick={handleToggleAccess}
                            sx={{
                                color: isUserDisabled ? '#2e7d32' : '#b71c1c',
                                fontWeight: 700,
                                gap: 1,
                            }}
                        >
                            {isUserDisabled
                                ? <CheckCircleOutlineIcon fontSize="small" />
                                : <BlockOutlinedIcon fontSize="small" />}
                            {isUserDisabled ? 'Habilitar' : 'Deshabilitar'}
                        </MenuItem>
                    )}
                </Menu>
            </Box>

            <Box className="user-header">
                <Box className="avatar" onClick={goToProfile}>
                    <img src={avatarSrc} alt="user-avatar" />
                </Box>

                <Box className="user-info">
                    <h3 onClick={goToProfile}>
                        {user?.name ?? 'Usuario'}
                    </h3>

                    <Box className="badges-row">
                        <Chip
                            size="small"
                            label={user?.role ?? 'Sin rol'}
                            sx={{
                                height: 22,
                                maxWidth: '100%',
                                fontSize: '0.68rem',
                                fontWeight: 900,
                                color: roleColor,
                                background: `${roleColor}16`,
                                border: `1px solid ${roleColor}35`,
                            }}
                        />

                        <Chip
                            size="small"
                            label={isUserDisabled ? 'Inactivo' : 'Activo'}
                            sx={{
                                height: 22,
                                fontSize: '0.66rem',
                                fontWeight: 900,
                                color: isUserDisabled ? '#b71c1c' : '#2e7d32',
                                background: isUserDisabled
                                    ? 'rgba(211, 47, 47, 0.10)'
                                    : 'rgba(46, 125, 50, 0.10)',
                                border: `1px solid ${isUserDisabled
                                    ? 'rgba(211, 47, 47, 0.32)'
                                    : 'rgba(46, 125, 50, 0.32)'}`,
                            }}
                        />
                    </Box>

                    {user?.company && (
                        <p
                            className="company"
                            title={`${user?.company?.name} ${user?.company?.lastname ?? ''}`}
                        >
                            <i className="fas fa-building"></i>
                            {user?.company?.name}
                        </p>
                    )}
                </Box>
            </Box>

            <StatsGrid>
                {stats.map((item) => (
                    <StatItem key={item.label}>
                        <Box className="stat-icon">
                            <i className={item.icon}></i>
                        </Box>

                        <Box className="stat-info">
                            <strong>{item.value}</strong>
                            <span>{item.label}</span>
                        </Box>
                    </StatItem>
                ))}
            </StatsGrid>
        </UserCardContent>

        <GiftMovesModal
            open={giftOpen}
            onClose={() => setGiftOpen(false)}
            targetUser={user}
            onSuccess={() => onChanged?.()}
        />

        <DeliverMovesModal
            open={deliverOpen}
            onClose={() => setDeliverOpen(false)}
            targetUser={user}
            onSuccess={() => onChanged?.()}
        />
        </>
    );
};

export default UserCard;

const UserCardContent = styled.div`
    width: 255px;
    min-height: 228px;
    position: relative;
    border-radius: 18px;
    background: ${({ $disabled }) =>
        $disabled
            ? 'linear-gradient(180deg, #fafafa 0%, #f1f3f5 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)'};
    border: 1px solid ${({ $disabled }) =>
        $disabled ? 'rgba(211, 47, 47, 0.22)' : 'rgba(15, 34, 77, 0.1)'};
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.07);
    overflow: hidden;
    transition: 0.22s ease;
    /* Atenúa visualmente la card cuando el usuario está inactivo:
       desatura los colores y baja la opacidad para que destaque del
       resto del listado sin esconder la información. */
    filter: ${({ $disabled }) => ($disabled ? 'grayscale(0.45)' : 'none')};
    opacity: ${({ $disabled }) => ($disabled ? 0.85 : 1)};

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 24px rgba(15, 34, 77, 0.1);
        border-color: ${({ $disabled }) =>
        $disabled ? 'rgba(211, 47, 47, 0.42)' : 'rgba(214, 170, 58, 0.42)'};
        filter: none;
        opacity: 1;
    }

    .badges-row {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        align-items: center;
    }

    .top-accent {
        height: 5px;
        width: 100%;
        background: ${({ $roleColor }) =>
        `linear-gradient(90deg, ${$roleColor} 0%, #d6aa3a 100%)`};
    }

    .options {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 5;

        button {
            width: 30px;
            height: 30px;
            color: #10285f;
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(15, 34, 77, 0.08);

            &:hover {
                background: rgba(214, 170, 58, 0.16);
            }
        }
    }

    .user-header {
        padding: 14px 14px 10px;
        display: flex;
        align-items: center;
        gap: 11px;
    }

    .avatar {
        width: 62px;
        height: 62px;
        flex-shrink: 0;
        border-radius: 50%;
        padding: 3px;
        background: rgba(214, 170, 58, 0.16);
        border: 2px solid rgba(214, 170, 58, 0.55);
        cursor: pointer;
        transition: 0.2s ease;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
            border-radius: 50%;
            background: #fff;
        }

        &:hover {
            transform: scale(1.04);
        }
    }

    .user-info {
        min-width: 0;
        padding-right: 24px;

        h3 {
            width: 100%;
            margin: 0 0 4px;
            color: #10285f;
            font-size: 0.98rem;
            font-weight: 900;
            line-height: 1.15;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        .company {
            margin-top: 5px;
            color: rgba(15, 34, 77, 0.62);
            font-size: 0.72rem;
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            i {
                color: #d6aa3a;
                flex-shrink: 0;
            }
        }
    }

    @media (max-width: 600px) {
        width: 100%;
        max-width: 285px;
        min-height: 210px;
    }
`;

const StatsGrid = styled.div`
    padding: 0 12px 14px; /* antes 0 12px 12px */
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
`;

const StatItem = styled.div`
    min-width: 0;
    padding: 8px;
    border-radius: 13px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    display: flex;
    align-items: center;
    gap: 7px;

    .stat-icon {
        width: 30px;
        height: 30px;
        flex-shrink: 0;
        border-radius: 10px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.26);
        display: flex;
        align-items: center;
        justify-content: center;

        i {
            color: #10285f;
            font-size: 0.78rem;
        }
    }

    .stat-info {
        min-width: 0;

        strong {
            display: block;
            color: #10285f;
            font-size: 0.9rem;
            font-weight: 900;
            line-height: 1.05;
        }

        span {
            display: block;
            margin-top: 1px;
            color: rgba(15, 34, 77, 0.58);
            font-size: 0.68rem;
            font-weight: 800;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
`;